import { useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useApps } from "../shared/api/useApps";
import { useRegistry } from "../shared/api/RegistryContext";
import { getToken } from "../shared/api/client";
import { T } from "../shared/styles/theme";

// The portal's own tab title (from index.html). A deep-linked child (a research company/timeline
// opened in a new tab) can override it via VQ_SET_TITLE; we restore this on every navigation so a
// company title never sticks after the user moves to another app.
const DEFAULT_TITLE = document.title || "ValueQuest — Nexus";

// Home (VQ Home at "/") passes its app in; nexus-home plays YouTube inline, so it gets more.
const HOME_ALLOW = "clipboard-write; autoplay; encrypted-media; picture-in-picture; fullscreen";

// VQ_NAVIGATE: a child asks the shell to open a portal path in THIS tab (VQ Home's "My shortcuts").
// Only a same-site path is honored — "/dashboard/..." — never a URL or a protocol-relative "//host";
// an unknown path lands on IframeView's own "no app → /" fallback, so nothing outside the registry
// can be opened this way.
function isPortalPath(path) {
  return typeof path === "string" && path.length <= 2000 && path.startsWith("/") && !path.startsWith("//")
    && !path.includes("\\");
}

export default function IframeView({ app: homeApp }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { data: apps, loading } = useApps();
  const { reload } = useRegistry();
  const iframeRef = useRef(null);

  // Match current path against registered apps: exact path wins (bare app URL); otherwise the
  // LONGEST registered prefix (deep link), so e.g. /dashboard/dealing/results would win over a
  // hypothetical /dashboard/dealing rather than depending on registry order.
  // The "/" app (VQ Home) is passed in by Home as homeApp and is never matched by path.
  const app = useMemo(() => {
    if (homeApp) return homeApp;
    const list = (apps || []).filter((a) => a.path !== "/");
    return (
      list.find((a) => a.path === location.pathname) ||
      list
        .filter((a) => location.pathname.startsWith(a.path + "/"))
        .sort((a, b) => b.path.length - a.path.length)[0] ||
      null
    );
  }, [homeApp, apps, location.pathname]);

  // Deep link = a path beyond the app's base (e.g. /dashboard/research/companies/1342); a bare
  // app path (or trailing slash) is not deep. Only a deep-linked child may set the tab title.
  const rest = app && !homeApp ? location.pathname.slice(app.path.length) : "";
  const deep = !!rest && rest !== "/";

  const sendToken = useCallback(
    async (targetOrigin) => {
      try {
        const idToken = await getToken(instance);
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: "VQ_AUTH_TOKEN",
            idToken,
            account: {
              name: accounts[0].name,
              username: accounts[0].username,
              idTokenClaims: accounts[0].idTokenClaims,
            },
          },
          targetOrigin
        );
      } catch (err) {
        console.error("Failed to acquire token for iframe:", err);
      }
    },
    [instance, accounts]
  );

  // Restore the portal's default tab title on every navigation; a deep-linked child re-asserts
  // its own title via VQ_SET_TITLE below.
  useEffect(() => {
    document.title = DEFAULT_TITLE;
  }, [location.pathname]);

  // Listen for messages from child iframe
  useEffect(() => {
    if (!app) return;

    const appOrigin = new URL(app.iframe_url).origin;

    function handleMessage(event) {
      // In dev, localhost origins match; in prod, validate against SWA origin
      if (event.origin !== appOrigin) return;

      if (
        event.data?.type === "VQ_CHILD_READY" ||
        event.data?.type === "VQ_TOKEN_REQUEST" ||
        event.data?.type === "VQ_REQUEST_TOKEN"
      ) {
        // child either just mounted or asked for a fresh token (its own expired)
        sendToken(appOrigin);
      } else if (
        event.data?.type === "VQ_SET_TITLE" &&
        typeof event.data.title === "string" &&
        deep
      ) {
        // A deep-linked (new-tab) child owns the browser tab title; the main embedded portal tab
        // keeps DEFAULT_TITLE, so we honor the child's title only for deep links.
        document.title = event.data.title;
      } else if (event.data?.type === "VQ_REGISTRY_CHANGED" && app.id === "portal-admin") {
        // Portal Admin saved a change to apps, areas, groups or a grant / revoke: refresh the
        // menu and Home tiles without reloading the portal. reload() keeps the
        // current data on screen, so this iframe is not unmounted.
        reload();
      } else if (event.data?.type === "VQ_NAVIGATE" && isPortalPath(event.data.path)) {
        navigate(event.data.path);
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [app, deep, sendToken, reload, navigate]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
          color: T.muted,
          fontSize: 14,
        }}
      >
        Loading...
      </div>
    );
  }

  // No access or app not found — redirect home
  if (!app) {
    return <Navigate to="/" replace />;
  }

  // Deep-link: forward the path (+ hash) beyond the app's base into the iframe's origin, so e.g.
  // /dashboard/research/companies/1342 loads {swa}/companies/1342 inside the iframe. The bare
  // path (no remainder) keeps the registered iframe_url unchanged. (rest/deep computed above.)
  // A category 'link' app (URL-only, no menu/card) keeps its iframe path instead:
  // /dashboard/soa/FC1/123 loads {swa}/soa/FC1/123.
  const base = app.category === "link"
    ? app.iframe_url.replace(/\/+$/, "")
    : new URL(app.iframe_url).origin;
  const src = deep ? base + rest + location.hash : app.iframe_url;

  return (
    <iframe
      ref={iframeRef}
      src={src}
      title={app.label}
      style={{
        flex: 1,
        width: "100%",
        border: "none",
        background: T.bg,
      }}
      allow={homeApp ? HOME_ALLOW : "clipboard-write"}
    />
  );
}
